import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Container, Tab, Tabs, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaletteIcon from '@mui/icons-material/Palette';
import ShieldIcon from '@mui/icons-material/Shield';
import { useTranslation } from 'react-i18next';
import ProfileDetailsTab from '@components/settings/ProfileDetailsTab';
import PasswordTab from '@components/settings/PasswordTab';
import NotificationsTab from '@components/settings/NotificationsTab';
import AppearanceTab from '@components/settings/AppearanceTab';
import DataPrivacyTab from '@components/settings/DataPrivacyTab';

/**
 * REV-491: one home for user configuration.
 *
 * Settings used to hold a single email-notifications toggle while /profile held
 * name, avatar, timezone AND password change. That split is what the
 * walkthrough hit as "Settings only has email preferences" — nothing was
 * missing so much as scattered. /profile now redirects here.
 *
 * Tab state lives in the URL so a tab is linkable and survives a reload,
 * matching OrganizationDetailPage (REV-486) and ContextDetailPage (REV-497)
 * rather than introducing a third pattern.
 */
const TAB_NAMES = ['profile', 'password', 'notifications', 'appearance', 'privacy'] as const;
type TabName = (typeof TAB_NAMES)[number];

/**
 * REV-685 renamed the "Security" tab to "Password", because the change-password
 * form is all it has ever contained.
 *
 * These names are not only labels — they are URL slugs, and the tab is
 * deliberately linkable (REV-491). Renaming the slug outright would quietly
 * break every existing bookmark and every link already shared to
 * /settings/security, which is far too high a price for a cosmetic rename.
 * The old name therefore keeps resolving.
 */
const LEGACY_TAB_ALIASES: Record<string, TabName> = { security: 'password' };

function getTabIndex(tabName: string | null): number {
  if (!tabName) return 0;
  const canonical = LEGACY_TAB_ALIASES[tabName] ?? tabName;
  const index = TAB_NAMES.indexOf(canonical as TabName);
  return index >= 0 ? index : 0;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return { id: `settings-tab-${index}`, 'aria-controls': `settings-tabpanel-${index}` };
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // /settings or /settings/:tab
  const pathParts = location.pathname.split('/');
  const activeTab = getTabIndex(pathParts.length > 2 ? (pathParts[2] ?? null) : null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    void navigate(`/settings/${TAB_NAMES[newValue]}`);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
        {t('settings.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('settings.subtitle')}
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          // REV-725: MUI's default `variant="standard"` squeezes and then
          // CLIPS tabs that do not fit. On Settings that hid Appearance and
          // Data & Privacy outright at narrow widths and truncated
          // Notifications — content removed, not reflowed, with no way to
          // reach it. Scrollable keeps every tab reachable at any width.
          // Same treatment OrganizationDetailPage already uses.
          //
          // Deliberately WITHOUT allowScrollButtonsMobile. Measured at 320px,
          // it renders two 40px buttons INSIDE a 256px strip, leaving a 176px
          // window for a 193px tab — so keyboard-focusing the last tab left it
          // only 38% visible. Without them the full width is available, the
          // tab lands fully in view, and narrow viewports get native swipe
          // scrolling, which is the right idiom there anyway. The buttons
          // still appear at wider widths that overflow.
          variant="scrollable"
          scrollButtons="auto"
          sx={{ maxWidth: '100%' }}
          value={activeTab}
          onChange={handleTabChange}
          aria-label={t('settings.title')}
        >
          <Tab
            icon={<PersonIcon />}
            iconPosition="start"
            label={t('settings.tabs.profile', 'Profile')}
            {...a11yProps(0)}
          />
          <Tab
            icon={<LockIcon />}
            iconPosition="start"
            label={t('settings.tabs.password', 'Password')}
            {...a11yProps(1)}
          />
          <Tab
            icon={<NotificationsIcon />}
            iconPosition="start"
            label={t('settings.tabs.notifications', 'Notifications')}
            {...a11yProps(2)}
          />
          <Tab
            icon={<PaletteIcon />}
            iconPosition="start"
            label={t('settings.tabs.appearance', 'Appearance')}
            {...a11yProps(3)}
          />
          <Tab
            icon={<ShieldIcon />}
            iconPosition="start"
            label={t('settings.tabs.privacy', 'Data & privacy')}
            {...a11yProps(4)}
          />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <ProfileDetailsTab />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <PasswordTab />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <NotificationsTab />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <AppearanceTab />
      </TabPanel>
      <TabPanel value={activeTab} index={4}>
        <DataPrivacyTab />
      </TabPanel>
    </Container>
  );
}
